import { Header, Sidebar } from '@/components/layout';
import { ClinicalTemplateDialog } from '@/components/templates/ClinicalTemplateDialog';
import { TemplateScopeTabs } from '@/components/templates/TemplateScopeTabs';
import { TodoCatalogTemplateDialog } from '@/components/templates/TodoCatalogTemplateDialog';
import {
  type ClinicalTemplate,
  type ClinicalTemplateFormState,
  type TodoCatalogFormState,
  type TodoCatalogPayload,
  type TodoCatalogTemplate,
  clinicalTemplatePayloadFromForm,
  emptyClinicalTemplateForm,
  emptyTodoCatalogForm,
  fetchJson,
  formFromTemplate,
  todoCatalogFormFromTemplate,
  todoCatalogPayloadFromForm,
} from '@/components/templates/template-utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Send, Trash2 } from 'lucide-react';
import { useState } from 'react';

type GlobalTemplate = ClinicalTemplate;

interface AdminClinic {
  id: number;
  name: string;
  auth_org_id?: string | null;
}

export default function TemplatesSystem() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [globalDialogOpen, setGlobalDialogOpen] = useState(false);
  const [editingGlobal, setEditingGlobal] = useState<GlobalTemplate | null>(null);
  const [globalForm, setGlobalForm] = useState<ClinicalTemplateFormState>(emptyClinicalTemplateForm);
  const [todoDialogOpen, setTodoDialogOpen] = useState(false);
  const [editingTodo, setEditingTodo] = useState<TodoCatalogTemplate | null>(null);
  const [todoForm, setTodoForm] = useState<TodoCatalogFormState>(emptyTodoCatalogForm);
  const [publishTemplate, setPublishTemplate] = useState<GlobalTemplate | null>(null);
  const [publishOrgId, setPublishOrgId] = useState('');
  const [loadingGlobalDetailId, setLoadingGlobalDetailId] = useState<number | null>(null);

  const { data: globalTemplates = [], isLoading: isLoadingGlobal } = useQuery({
    queryKey: ['templates', 'global'],
    queryFn: () => fetchJson<GlobalTemplate[]>('/api/templates/global'),
  });
  const { data: todoCatalogPayload, isLoading: isLoadingTodo } = useQuery({
    queryKey: ['templates', 'todo-catalog'],
    queryFn: () => fetchJson<{ templates?: TodoCatalogTemplate[] }>('/api/templates/todo/catalog'),
  });
  const todoCatalog = todoCatalogPayload?.templates || [];
  const { data: adminClinics = [], isLoading: isLoadingClinics } = useQuery({
    queryKey: ['admin', 'clinics'],
    queryFn: () => fetchJson<AdminClinic[]>('/api/admin/clinics'),
  });
  const publishableOrgs = adminClinics.filter(
    (clinic): clinic is AdminClinic & { auth_org_id: string } =>
      typeof clinic.auth_org_id === 'string' && clinic.auth_org_id.length > 0
  );

  const globalMutation = useMutation({
    mutationFn: ({ id, data }: { id?: number; data: Partial<GlobalTemplate> }) =>
      id
        ? fetchJson<GlobalTemplate>(`/api/templates/global/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
          })
        : fetchJson<GlobalTemplate>('/api/templates/global', {
            method: 'POST',
            body: JSON.stringify(data),
          }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates', 'global'] });
      setGlobalDialogOpen(false);
      setEditingGlobal(null);
      setGlobalForm(emptyClinicalTemplateForm);
      toast({ title: 'Kerfissniðmát vistað' });
    },
    onError: () => {
      toast({ title: 'Villa', description: 'Ekki tókst að vista kerfissniðmát.', variant: 'destructive' });
    },
  });

  const deleteGlobalMutation = useMutation({
    mutationFn: (id: number) => fetchJson(`/api/templates/global/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates', 'global'] });
      toast({ title: 'Kerfissniðmáti eytt' });
    },
    onError: () => {
      toast({ title: 'Villa', description: 'Ekki tókst að eyða kerfissniðmáti.', variant: 'destructive' });
    },
  });

  const publishMutation = useMutation({
    mutationFn: ({ id, authOrgId }: { id: number; authOrgId: string }) =>
      fetchJson(`/api/templates/global/${id}/publish`, {
        method: 'POST',
        body: JSON.stringify({ auth_org_id: authOrgId }),
      }),
    onSuccess: () => {
      setPublishTemplate(null);
      setPublishOrgId('');
      toast({ title: 'Kerfissniðmát birt stofnun' });
    },
    onError: () => {
      toast({ title: 'Villa', description: 'Ekki tókst að birta kerfissniðmát.', variant: 'destructive' });
    },
  });

  const todoMutation = useMutation({
    mutationFn: ({ id, data }: { id?: number; data: Partial<TodoCatalogPayload> }) =>
      id
        ? fetchJson<{ template: TodoCatalogTemplate }>(`/api/templates/todo/catalog/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
          })
        : fetchJson<{ template: TodoCatalogTemplate }>('/api/templates/todo/catalog', {
            method: 'POST',
            body: JSON.stringify(data),
          }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates', 'todo-catalog'] });
      setTodoDialogOpen(false);
      setEditingTodo(null);
      setTodoForm(emptyTodoCatalogForm);
      toast({ title: 'Verkefnasnið vistað' });
    },
    onError: () => {
      toast({ title: 'Villa', description: 'Ekki tókst að vista verkefnasnið.', variant: 'destructive' });
    },
  });

  const openGlobalDialog = async (template?: GlobalTemplate) => {
    if (!template) {
      setEditingGlobal(null);
      setGlobalForm(emptyClinicalTemplateForm);
      setGlobalDialogOpen(true);
      return;
    }

    setLoadingGlobalDetailId(template.id);
    try {
      const detail = await fetchJson<ClinicalTemplate>(`/api/templates/${template.id}`);
      setEditingGlobal(detail);
      setGlobalForm(formFromTemplate(detail));
      setGlobalDialogOpen(true);
    } catch {
      toast({ title: 'Villa', description: 'Ekki tókst að sækja kerfissniðmát.', variant: 'destructive' });
    } finally {
      setLoadingGlobalDetailId(null);
    }
  };

  const openTodoDialog = (template?: TodoCatalogTemplate) => {
    setEditingTodo(template || null);
    setTodoForm(todoCatalogFormFromTemplate(template));
    setTodoDialogOpen(true);
  };

  const saveGlobalTemplate = (formValues: ClinicalTemplateFormState) => {
    globalMutation.mutate({
      id: editingGlobal?.id,
      data: clinicalTemplatePayloadFromForm(formValues, { includeGlobalFlags: true }),
    });
  };

  const saveTodoTemplate = (formValues: TodoCatalogFormState) => {
    todoMutation.mutate({
      id: editingTodo?.id,
      data: todoCatalogPayloadFromForm(formValues, Boolean(editingTodo)),
    });
  };

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header title="Kerfissniðmát" />
        <main className="flex-1 overflow-y-auto p-8">
          <div className="mx-auto max-w-6xl space-y-6">
            <TemplateScopeTabs active="system" />

            <div>
              <h1 className="font-bold text-2xl text-slate-900">Kerfissniðmát</h1>
              <p className="text-muted-foreground text-sm">Stýring á klínískum kerfissniðmátum og verkefnasniðmátum.</p>
            </div>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-4">
                <CardTitle className="text-lg">Klínísk kerfissniðmát</CardTitle>
                <Button onClick={() => openGlobalDialog()} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Nýtt kerfissniðmát
                </Button>
              </CardHeader>
              <CardContent>
                {isLoadingGlobal ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : globalTemplates.length === 0 ? (
                  <div className="rounded-md border p-4 text-muted-foreground text-sm">
                    Engin kerfissniðmát fundust.
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nafn</TableHead>
                        <TableHead>Lýsing</TableHead>
                        <TableHead>Sjálfvirk afritun</TableHead>
                        <TableHead className="text-right">Aðgerðir</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {globalTemplates.map((template) => (
                        <TableRow key={template.id}>
                          <TableCell className="font-medium">{template.name}</TableCell>
                          <TableCell className="text-muted-foreground">{template.description || '-'}</TableCell>
                          <TableCell>
                            <Badge variant={template.auto_copy_for_new_users ? 'default' : 'secondary'}>
                              {template.auto_copy_for_new_users ? 'Virkt' : 'Óvirkt'}
                            </Badge>
                          </TableCell>
                          <TableCell className="space-x-1 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`Breyta ${template.name}`}
                              onClick={() => openGlobalDialog(template)}
                              disabled={loadingGlobalDetailId === template.id}
                            >
                              {loadingGlobalDetailId === template.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Pencil className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`Birta ${template.name}`}
                              onClick={() => setPublishTemplate(template)}
                              className="cursor-pointer"
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`Eyða ${template.name}`}
                              onClick={() => deleteGlobalMutation.mutate(template.id)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-4">
                <CardTitle className="text-lg">Verkefnasniðmát</CardTitle>
                <Button onClick={() => openTodoDialog()} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Nýtt verkefnasnið
                </Button>
              </CardHeader>
              <CardContent>
                {isLoadingTodo ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : todoCatalog.length === 0 ? (
                  <div className="rounded-md border p-4 text-muted-foreground text-sm">Engin verkefnasnið fundust.</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tegund</TableHead>
                        <TableHead>Nafn</TableHead>
                        <TableHead>Staða</TableHead>
                        <TableHead className="text-right">Aðgerðir</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {todoCatalog.map((template) => (
                        <TableRow key={template.id}>
                          <TableCell className="font-mono">{template.todo_type}</TableCell>
                          <TableCell className="font-medium">{template.template_name}</TableCell>
                          <TableCell>
                            <Badge variant={template.is_active ? 'default' : 'secondary'}>
                              {template.is_active ? 'Virkt' : 'Óvirkt'}
                            </Badge>
                          </TableCell>
                          <TableCell className="space-x-1 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`Breyta ${template.template_name}`}
                              onClick={() => openTodoDialog(template)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                todoMutation.mutate({
                                  id: template.id,
                                  data: { is_active: !template.is_active },
                                })
                              }
                            >
                              {template.is_active ? 'Afvirkja' : 'Virkja'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>

      <ClinicalTemplateDialog
        open={globalDialogOpen}
        onOpenChange={setGlobalDialogOpen}
        title={editingGlobal ? 'Breyta kerfissniðmáti' : 'Nýtt kerfissniðmát'}
        description="Kerfissniðmát eru grunnsniðmát sem kerfisstjórar geta birt til stofnana."
        form={globalForm}
        onSubmit={saveGlobalTemplate}
        isSaving={globalMutation.isPending}
        showCancelButton={false}
        showGlobalFlags
      />

      <TodoCatalogTemplateDialog
        open={todoDialogOpen}
        onOpenChange={setTodoDialogOpen}
        form={todoForm}
        isEditing={Boolean(editingTodo)}
        isSaving={todoMutation.isPending}
        onSubmit={saveTodoTemplate}
      />

      <Dialog
        open={Boolean(publishTemplate)}
        onOpenChange={() => {
          setPublishTemplate(null);
          setPublishOrgId('');
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Birta kerfissniðmát</DialogTitle>
            <DialogDescription>Veldu stofnunina sem á að fá afrit.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5">
            <Label htmlFor="publish-auth-org-id">Stofnun</Label>
            <Select
              value={publishOrgId}
              onValueChange={setPublishOrgId}
              disabled={isLoadingClinics || publishableOrgs.length === 0}
            >
              <SelectTrigger id="publish-auth-org-id" aria-label="Stofnun">
                <SelectValue placeholder={isLoadingClinics ? 'Sæki stofnanir...' : 'Veldu stofnun'} />
              </SelectTrigger>
              <SelectContent>
                {publishableOrgs.map((org) => (
                  <SelectItem key={org.auth_org_id} value={org.auth_org_id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!isLoadingClinics && publishableOrgs.length === 0 && (
              <p className="text-muted-foreground text-sm">Engar stofnanir með auth org id fundust.</p>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={() =>
                publishTemplate && publishMutation.mutate({ id: publishTemplate.id, authOrgId: publishOrgId.trim() })
              }
              disabled={publishMutation.isPending || !publishOrgId.trim()}
            >
              {publishMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Birta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
